import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ActionButton from 'components/Widget/ActionButton';
import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';
import { ExportFramesAiPreset, MAX_LLM_EXPORT_FRAMES } from 'dragonmouse-shared/api/export';
import { FrameStatus } from 'dragonmouse-shared/interfaces';

interface Props {
	label: string;
	preset: ExportFramesAiPreset;
	customBrief?: string;
	frameIds: string[];
}

const ExportAiAction = ({ label, preset, customBrief, frameIds }: Props) => {
	const [isExporting, setIsExporting] = useState(false);
	const { activeScene, allFrames } = useSelector(
		(r: RootReducer) => ({
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			allFrames: selectFrames(r.db),
		}),
		shallowEqual
	);

	const framesFound = frameIds
		.map((id) => allFrames.find((f) => f.id === id))
		.filter((f): f is NonNullable<typeof f> => !!f);
	const allReady =
		framesFound.length === frameIds.length &&
		framesFound.every((f) => f.metadata.status === FrameStatus.READY);

	const onClick = async () => {
		if (isExporting) return;
		if (!activeScene) return;
		if (frameIds.length === 0) {
			toast.error('No frames selected to export.');
			return;
		}
		if (framesFound.length < frameIds.length) {
			toast.error('Some frames are no longer available.');
			return;
		}
		if (!allReady) return;
		if (frameIds.length > MAX_LLM_EXPORT_FRAMES) {
			toast.error(`AI export is limited to ${MAX_LLM_EXPORT_FRAMES} frames.`);
			return;
		}

		const filename = buildExportFilename(activeScene.title, preset, 'document', 'md');
		setIsExporting(true);
		try {
			await runExportWithToast({
				pendingLabel: 'Writing your .md — this may take a minute or two…',
				run: async () => {
					const { exportId } = await forge.exportFramesAi({
						frameIds,
						preset,
						customBrief,
						filename,
					});
					return { exportId, filename };
				},
			});
		} catch (e) {
			Sentry.captureException(e);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<ActionButton
			icon={IconType.DOWNLOAD}
			label={label}
			loadingLabel="Exporting frames…"
			isLoading={isExporting}
			disabledReason={!allReady ? 'Waiting for frames to finish generating.' : undefined}
			onClick={onClick}
		/>
	);
};

export default ExportAiAction;
